import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Icon, IconButton, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaClipboardList, FaTrash } from "react-icons/fa";
import type { SelectionCriterionDto } from "../../../../../autogen/bff-api";
import { rowOpenProps } from "../../../../../common/clickableRow";
import { useRequirementDescription } from "../../../../../common/library/describeRequirement";

interface Props {
  items: SelectionCriterionDto[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  readOnly?: boolean;
  /** Marks the last n rows as just added, so a batch from the library is easy to spot and undo. */
  markedCount?: number;
  /**
   * How the rows are drawn.
   *
   * - `cards` — separate cards with space between them, each carrying its own border. What a
   *   procurement's own list uses, where the rows are being worked on one at a time.
   * - `framed` — one frame with hairline-divided rows, the way the library draws its criteria. A
   *   library list is read far more often than it is edited, and a long stack of cards is harder to
   *   scan than a table. The delete action stays visible there rather than waiting for a hover.
   */
  layout?: "cards" | "framed";
}

// t('No requirements added yet') t('Add qualification requirements or choose a template')
// t('Add requirement') t('Requires documentation')
// t('Delete requirement')

export const QualificationRequirementList = ({
  items,
  onAdd,
  onEdit,
  onDelete,
  readOnly = false,
  markedCount = 0,
  layout = "cards",
}: Props) => {
  const { t } = useTranslation();
  const describe = useRequirementDescription();
  const firstMarked = items.length - markedCount;
  const isFramed = layout === "framed";

  if (items.length === 0) {
    return (
      <Box
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        bg="smBackgroundSecondary"
        py="12"
        px="6"
        textAlign="center"
      >
        <Icon as={FaClipboardList} boxSize="10" color="smMuted" mb="4" />
        <Text fontWeight="bold" mb="1">
          {t("No requirements added yet")}
        </Text>
        {!readOnly && (
          <>
            <Text color="smMuted" fontSize="sm" mb="6">
              {t("Add qualification requirements or choose a template")}
            </Text>
            <Button
              colorScheme="teal"
              leftIcon={<AddIcon />}
              onClick={onAdd}
            >
              {t("Add requirement")}
            </Button>
          </>
        )}
      </Box>
    );
  }

  const rows = items.map((item, index) => {
    const detail = describe.selection(item);
    const isMarked = index >= firstMarked;
    return (
      <Box
        key={`${item.type}-${item.name}-${index}`}
        data-testid="requirement-row"
        px="4"
        py="3"
        // A frame draws the edges once and separates the rows with a hairline; a card carries its own.
        // Marked rows say so with a tint inside a frame, since there is no border of their own to give
        // a colour to.
        {...(isFramed
          ? {
              borderBottomWidth: index === items.length - 1 ? "0" : "1px",
              borderColor: "smBorderSubtle",
              bg: isMarked ? "smSuccessSoft" : undefined,
            }
          : {
              borderWidth: isMarked ? "1.5px" : "1px",
              borderColor: isMarked ? "smSuccessSoftBorder" : "smBorder",
              borderRadius: "lg",
              bg: "smBackground",
            })}
        // data-group, not role="group": cardOpenProps makes the row a button, and _groupHover
        // below matches either — so the actions still appear on hover.
        data-group
        _hover={
          isFramed
            ? readOnly
              ? undefined
              : { bg: "smBackgroundSecondary" }
            : readOnly
              ? { borderColor: "smMuted" }
              : { borderColor: "smMuted", bg: "smBackgroundSecondary" }
        }
        {...(readOnly ? {} : rowOpenProps(() => onEdit(index)))}
      >
        <Flex justify="space-between" align="center" gap="2">
          <Box flex="1" minW="0">
            <Text fontWeight="bold" fontSize="sm" color="smText">
              {item.name}
            </Text>
            {detail && (
              <Text fontSize="xs" color="smMuted" noOfLines={2}>
                {detail}
              </Text>
            )}
          </Box>
          {!readOnly && (
            <Flex
              gap="1"
              ml="2"
              flexShrink={0}
              // In a frame the action stands where the criteria lists keep theirs, rather than
              // appearing under the cursor: a library row is scanned more often than it is opened.
              // Stated as an attribute too, because the opacity that hides it is emotion's and a test
              // cannot read it back.
              data-reveal={isFramed ? "always" : "hover"}
              {...(isFramed
                ? {}
                : {
                    opacity: 0,
                    _groupHover: { opacity: 1 },
                    _focusWithin: { opacity: 1 },
                    transition: "opacity 0.15s",
                  })}
            >
              <IconButton
                aria-label={t("Delete requirement")}
                icon={<FaTrash />}
                size={isFramed ? "xs" : "sm"}
                variant="ghost"
                colorScheme="red"
                onClick={() => onDelete(index)}
              />
            </Flex>
          )}
        </Flex>
      </Box>
    );
  });

  return isFramed ? (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" overflow="hidden" bg="smBackground">
      {rows}
    </Box>
  ) : (
    <VStack spacing="2" align="stretch">
      {rows}
    </VStack>
  );
};
